import React, { useEffect, useState } from "react";
import Sidebar from "../Sidebar/Sidebar";
import Navbar from "./Navbar";
import Header from "./header";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import ApiConfig from "../../common/config/ApiConfig";
import { useLanguage } from "../../common/i18n/LanguageContext";

const MARKET_OPTIONS = [
  { value: "th_gov", labelKey: "table_lotto_thai" },
  { value: "laos_gov", labelKey: "table_lotto_laos" },
  { value: "hanoy_PhuQua", labelKey: "lotto_type_hanoi" },
  { value: "Yeekee_5", labelKey: "table_lotto_yeekee5" },
  { value: "Yeekee_10", labelKey: "table_lotto_yeekee10" },
  { value: "Yeekee_15", labelKey: "table_lotto_yeekee15" },
];

const THAI_DIGIT_GROUPS = {
  four: { labelKey: "bet_group_four_digit", types: ["four_top", "four_tod"] },
  three: {
    labelKey: "bet_group_three_digit",
    types: ["three_top", "three_front", "three_bot", "three_tod"],
  },
  two: {
    labelKey: "bet_group_two_digit",
    types: ["two_top", "two_bot", "run_top", "run_bot"],
  },
};

const OTHER_DIGIT_GROUPS = {
  three: {
    labelKey: "bet_group_three_digit",
    types: ["three_top", "three_tod"],
  },
  two: {
    labelKey: "bet_group_two_digit",
    types: ["two_top", "two_bot", "run_top", "run_bot"],
  },
};

const LIMIT = 10;

const formatAmount = (value) => {
  const floatValue = parseFloat(value);
  return isNaN(floatValue)
    ? "-"
    : floatValue.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
};

function LottoBetPage() {
  const { t } = useLanguage();
  const pos = localStorage.getItem("position");
  const user = localStorage.getItem("user");

  const [market, setMarket] = useState("th_gov");
  const [drawDate, setDrawDate] = useState(moment().format("YYYY-MM-DD"));
  const [number, setNumber] = useState("");
  const [digitGroups, setDigitGroups] = useState({
    four: true,
    three: true,
    two: true,
  });

  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [currencyCodeById, setCurrencyCodeById] = useState({});

  useEffect(() => {
    ApiConfig.get("/getCurrencyList").then((result) => {
      if (result.resCode !== 200) return;
      const list = Array.isArray(result.data) ? result.data : [];
      setCurrencyCodeById(Object.fromEntries(list.map((c) => [c.id, c.code])));
    });
  }, []);

  const digitGroupOptions =
    market === "th_gov" ? THAI_DIGIT_GROUPS : OTHER_DIGIT_GROUPS;

  const toggleDigitGroup = (key) => {
    setDigitGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const fetchData = (currentPage) => {
    const betTypes = Object.keys(digitGroupOptions)
      .filter((key) => digitGroups[key])
      .flatMap((key) => digitGroupOptions[key].types);

    const params = new URLSearchParams({
      user,
      lotto_type: market,
      drawdate: drawDate,
      number,
      page: currentPage,
      limit: LIMIT,
    });
    // qs (Express's query parser) only returns an array for a repeated key
    // — the `bet_type[]=` bracket form forces an array even when only one
    // type is selected (matches /sumorder's lottotype[] fix).
    betTypes.forEach((type) => params.append("bet_type[]", type));
    ApiConfig.get(`/getlottobet?${params.toString()}`).then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      setData(Array.isArray(result.data) ? result.data : []);
      setPage(currentPage);
      if (result.total != null) setTotal(result.total);
    });
  };

  useEffect(() => {
    fetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    fetchData(1);
  };

  const totalPages = total > 0 ? Math.ceil(total / LIMIT) : 0;

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <div className="shrink-0">
        <Navbar />
      </div>
      <div className="flex flex-1 overflow-hidden items-start">
        <div className="shrink-0 overflow-y-auto h-full">
          <Sidebar pos={pos} />
        </div>
        <div className="flex flex-col flex-1 max-h-full overflow-hidden">
          <div className="shrink-0">
            <Header title={t("menu_bet_amount")} />
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            <div className="p-2 bg-amber-50 border-[1px] border-slate-400 rounded-lg mb-2 text-sm">
              <div className="flex flex-row flex-wrap items-center gap-4">
                <div className="flex flex-row items-center gap-2">
                  <label className="font-bold">
                    {t("lottobet_market_label")}
                  </label>
                  <select
                    value={market}
                    onChange={(e) => setMarket(e.target.value)}
                    className="p-1 border border-gray-300 rounded"
                  >
                    {MARKET_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {t(opt.labelKey)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-row items-center gap-2">
                  <label className="font-bold">
                    {t("lottobet_draw_date_label")}
                  </label>
                  <input
                    type="date"
                    value={drawDate}
                    onChange={(e) => setDrawDate(e.target.value)}
                    className="p-1 border border-gray-300 rounded"
                  />
                </div>

                <div className="flex flex-row items-center gap-2">
                  <label className="font-bold">
                    {t("common_number_label")}
                  </label>
                  <input
                    type="text"
                    value={number}
                    onChange={(e) => setNumber(e.target.value)}
                    className="p-1 border border-gray-300 rounded w-24"
                  />
                </div>

                <div className="flex flex-row items-center gap-3">
                  <label className="font-bold">
                    {t("lottobet_bet_type_label")}
                  </label>
                  {Object.entries(digitGroupOptions).map(([key, group]) => (
                    <label key={key} className="flex items-center gap-1">
                      <input
                        type="checkbox"
                        className="accent-blue-500"
                        checked={digitGroups[key]}
                        onChange={() => toggleDigitGroup(key)}
                      />
                      {t(group.labelKey)}
                    </label>
                  ))}
                </div>

                <button
                  onClick={handleSearch}
                  className="py-1 px-3 bg-brand-blue-700 hover:bg-brand-blue-800 text-white font-bold rounded text-sm"
                >
                  {t("common_search")}
                </button>
              </div>
            </div>

            <div className="overflow-x-auto rounded">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-brand-blue-800 text-white">
                    <th className="px-3 py-2 text-left">
                      {t("common_number_label")}
                    </th>
                    <th className="px-3 py-2 text-right">
                      {t("waitresult_bet_wager_label")}
                    </th>
                    <th className="px-3 py-2 text-right">
                      {t("waitresult_bet_payout_label")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.length === 0 ? (
                    <tr>
                      <td
                        colSpan={3}
                        className="text-center py-4 text-gray-400"
                      >
                        {t("common_no_data")}
                      </td>
                    </tr>
                  ) : (
                    data.map((row, index) => (
                      <tr
                        key={`${row.number}-${index}`}
                        className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                      >
                        <td className="px-3 py-2">
                          <span className="font-bold">{row.number}</span>
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-sky-600">
                          {formatAmount(row.bet_amount)}{" "}
                          {currencyCodeById[row.currency_id]}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-red-600">
                          {formatAmount(row.payrateAmount)}{" "}
                          {currencyCodeById[row.currency_id]}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {(totalPages > 1 || data.length > 0) && (
              <div className="flex items-center justify-between mt-3 px-2">
                <span className="text-sm font-semibold text-brand-blue-700 bg-brand-blue-50 px-3 py-1 rounded-full border border-brand-blue-200">
                  {t("commission_page_label")} {page}
                  {totalPages > 0 ? ` / ${totalPages}` : ""}
                  {total > 0 && ` · ${total} ${t("common_items_unit")}`}
                </span>
                <div className="flex space-x-1">
                  <button
                    onClick={() => fetchData(1)}
                    disabled={page === 1}
                    className="px-2 py-1 text-sm font-bold rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    «
                  </button>
                  <button
                    onClick={() => fetchData(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 text-sm font-medium rounded bg-brand-blue-100 text-brand-blue-700 hover:bg-brand-blue-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    ‹ {t("common_page_previous")}
                  </button>
                  <span className="px-3 py-1 text-sm font-bold rounded bg-brand-blue-600 text-white select-none">
                    {page}
                  </span>
                  <button
                    onClick={() => fetchData(page + 1)}
                    disabled={totalPages > 0 && page >= totalPages}
                    className="px-3 py-1 text-sm font-medium rounded bg-brand-blue-100 text-brand-blue-700 hover:bg-brand-blue-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    {t("common_next")} ›
                  </button>
                  {totalPages > 0 && (
                    <button
                      onClick={() => fetchData(totalPages)}
                      disabled={page >= totalPages}
                      className="px-2 py-1 text-sm font-bold rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      »
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LottoBetPage;
